import { useEffect, useState } from 'react'
import { Icon } from './Icon'
import { DiffView } from './DiffView'
import {
  api,
  isBlocking,
  type BranchInfo,
  type Member,
  type PreviewLink,
  type Problem,
  type ReviewThread,
  type StagedChange,
} from '../api'
import { useDialog } from '../useDialog'
import { draftLabel } from '../drafts'

/**
 * Review before committing.
 *
 * The first pass squeezed this into a strip along the bottom, which meant the
 * diff — the one thing a reviewer must actually read — was a tab somewhere else.
 * Here the change list, the diff and the message sit together, because that is
 * the decision being made.
 */
/**
 * The whole address to send somebody, from the path the API returns.
 *
 * Shown as well as copied: a person who reads the link before sending it
 * should see the same thing the clipboard has, and a bare path is not
 * something anybody can paste into a message.
 */
function shareUrl(path: string | undefined, siteUrl: string | null): string {
  return new URL(path ?? '', siteUrl || window.location.origin).href
}

interface Props {
  open: boolean
  branch: string | null
  /**
   * Every draft there is, for the picker in this drawer's head.
   *
   * The picker lived in the top bar, where it made "which draft am I on" a
   * permanent question over every screen. It is HERE now because this drawer
   * is where a draft's work is read, judged and published — the one surface
   * that is about the draft itself. The auto-draft model replaces most uses
   * of it later; today it only moved.
   */
  branches: BranchInfo[]
  /** Move to another draft, or to the read-only live site (null). */
  onPickDraft: (name: string | null) => void
  /** Ask for a new draft, by name. Closes this drawer first — one question at a time. */
  onNewDraft: () => void
  staged: StagedChange[]
  problems: Problem[]
  /**
   * Run the repair for the redirects that cannot work.
   *
   * Absent for somebody who may not write — the repair stages a change, so
   * offering it to a viewer would be a button that can only be refused.
   */
  onTidyRedirects?: () => void
  diff: string
  busy: boolean
  pr: { number: number; url: string } | null
  /**
   * Where the published site answers, when the deployment says.
   *
   * A preview is served by the SITE, not by this app, so a link built against
   * the app's own origin points at a host that does not serve previews — it
   * answers with the editor. Null in a single-origin deployment, where the
   * current origin is the right answer.
   */
  siteUrl: string | null
  /** The signed-in person's id, to keep verdict buttons off their own draft. */
  youId: string | null
  people: Member[]
  /** Owner, admin, or a writer holding the publish grant. */
  mayVerdict: boolean
  /** True once the draft has a commit — a review needs something to review. */
  committed: boolean
  /**
   * The frozen version this draft is FOR, as a reader would name it, or null
   * for the live documentation.
   *
   * The footer said "Publishing puts this on the live site" to everybody,
   * which is the opposite of the truth for a draft aimed at a version: the
   * work lands in `v1` and the live documentation does not change. That is the
   * one sentence somebody reads before pressing the irreversible button, and
   * for these drafts it was wrong — the only place the product said otherwise
   * was a `title` on a badge in the top bar, which nothing but a mouse could
   * read.
   */
  aimedAt: string | null
  onClose: () => void
  onCommit: (message: string) => void
  onPush: () => void
  /** Absent when this person may not merge, which hides the button. */
  onMerge?: () => void
  onUnstage: (path: string) => void
  /**
   * The door out of a review with nothing in it: the page being written.
   * Absent for anybody who cannot stage a change, so the panel says its
   * sentence and offers nothing it could not honour.
   */
  onWrite?: () => void
}

const VERB: Record<StagedChange['op'], string> = {
  write: 'edited',
  move: 'moved',
  delete: 'deleted',
}

export function ReviewDrawer({
  open,
  branch,
  branches,
  onPickDraft,
  onNewDraft,
  staged,
  problems,
  onTidyRedirects,
  diff,
  busy,
  pr,
  siteUrl,
  youId,
  people,
  mayVerdict,
  committed,
  aimedAt,
  onClose,
  onCommit,
  onPush,
  onMerge,
  onUnstage,
  onWrite,
}: Props) {
  /**
   * Merging asks twice.
   *
   * It is the only irreversible step in the product — it puts words in front of
   * readers — and it sits next to Commit and Push, which are not. A second click
   * costs a moment and prevents the one mistake here that cannot be undone by
   * pressing something else.
   */
  const [confirming, setConfirming] = useState(false)
  const [message, setMessage] = useState('')
  const [reviewer, setReviewer] = useState('')
  /**
   * The link, held until this drawer closes.
   *
   * Only a hash of the token is stored, so this is the only place it will ever
   * exist — the same reason the invitation panel shows its link once.
   */
  const [link, setLink] = useState<PreviewLink | null>(null)
  const [copied, setCopied] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)

  const [thread, setThread] = useState<ReviewThread | null>(null)
  /**
   * The commit this panel was DRAWN from, held apart from the thread.
   *
   * `thread` is replaced by the response to every review action, and each of
   * those responses carries a fresh head. If the verdict took its sha from
   * there, a reviewer who read the diff at one commit, left a comment after
   * the author had committed again, and then pressed Approve would be
   * approving the new commit — because the comment quietly re-baselined what
   * the panel claimed to have read, while the diff beside it still showed the
   * old words.
   *
   * So this is set when the drawer opens on a branch, and by nothing else.
   * Reopening is what re-reads, and reopening also redraws the diff — the two
   * move together, which is the whole point.
   */
  const [readSha, setReadSha] = useState<string | null>(null)

  /**
   * The diff, as words. A unified Markdown diff makes a reviewer read syntax;
   * this renders the live page and the draft page through the SAME renderer
   * the published site uses and puts them side by side. Text stays one click
   * away for the people who want the exact characters.
   */
  const [diffMode, setDiffMode] = useState<'rendered' | 'text'>('rendered')
  const changedPages = staged.filter((s) => s.path.endsWith('.md')).map((s) => s.path)
  const [comparePath, setComparePath] = useState<string | null>(null)
  const [compared, setCompared] = useState<{ before: string | null; after: string | null } | null>(
    null,
  )
  /**
   * The comparison that did not arrive, told apart from the one that has not
   * arrived yet.
   *
   * Both used to be `compared === null`, and null was read INSIDE the two
   * panes — so while the request was in flight the left one said "New page —
   * nothing on the live site yet" and the right one said "Deleted on this
   * draft", at the same time, about the same page. A page cannot be both, and
   * a failed compare said it too.
   */
  const [compareError, setCompareError] = useState<string | null>(null)
  const shownPath = comparePath ?? changedPages[0] ?? null

  useEffect(() => {
    if (!open || !branch || diffMode !== 'rendered' || !shownPath) {
      setCompared(null)
      setCompareError(null)
      return
    }
    let gone = false
    setCompared(null)
    setCompareError(null)
    void api
      .compare(branch, shownPath)
      .then((r) => {
        if (!gone) setCompared(r)
      })
      .catch((e: unknown) => {
        if (!gone) setCompareError(e instanceof Error ? e.message : String(e))
      })
    return () => {
      gone = true
    }
  }, [open, branch, diffMode, shownPath, staged.length])
  const [reviewBusy, setReviewBusy] = useState(false)
  const [reviewError, setReviewError] = useState<string | null>(null)
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!open || !branch) {
      setThread(null)
      setReadSha(null)
      return
    }
    let gone = false
    void api
      .reviewThread(branch)
      .then((t) => {
        if (gone) return
        setThread(t)
        setReadSha(t.headSha)
      })
      .catch(() => undefined) // a draft with no thread yet is not an error
    return () => {
      gone = true
    }
  }, [open, branch])

  const act = async (work: () => Promise<ReviewThread>) => {
    setReviewBusy(true)
    setReviewError(null)
    try {
      setThread(await work())
      setNote('')
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : String(e))
    } finally {
      setReviewBusy(false)
    }
  }

  const share = async () => {
    if (!branch) return
    setSharing(true)
    setShareError(null)
    try {
      setLink(await api.createPreview(branch))
    } catch (e) {
      setShareError(e instanceof Error ? e.message : String(e))
    } finally {
      setSharing(false)
    }
  }
  const panel = useDialog<HTMLElement>(open, onClose)
  const blocking = problems.filter(isBlocking)
  /** The blocking problems the product itself can repair — the server says which. */
  const repairable = blocking.filter((problem) => problem.repair === 'redirects')
  const canCommit = staged.length > 0 && blocking.length === 0 && message.trim().length >= 3

  useEffect(() => {
    if (open) return
    // Cleared on close. The token is shown once, and a panel that still displays
    // it after being dismissed and reopened suggests it can be found again.
    setLink(null)
    setCopied(false)
    setShareError(null)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canCommit) {
        e.preventDefault()
        onCommit(message.trim())
        setMessage('')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, canCommit, message, onCommit, onClose])

  if (!open) return null

  return (
    <div className="scrim" onMouseDown={onClose}>
      <aside
        className="drawer"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Changes"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="drawer__head">
          <div>
            <h2 className="drawer__title">Changes</h2>
            {/* Which draft this review is about — and the way onto another
                one, or onto a first one from the read-only live site. */}
            <div className="drawer__draftpick">
              <div className={`branchpick${branch ? '' : ' branchpick--readonly'}`}>
                <Icon name="branch" size={13} />
                <select
                  className="branchpick__select truncate"
                  value={branch ?? ''}
                  aria-label="Draft"
                  onChange={(e) => onPickDraft(e.target.value || null)}
                >
                  <option value="">{draftLabel(null)} — read only</option>
                  {branches
                    // A published draft is finished: its work is live, and
                    // offering it here is offering a choice that cannot work.
                    // The current one stays listed even when finished, so the
                    // selector never shows a value it does not contain.
                    .filter(
                      (b) => b.name === branch || (b.status !== 'merged' && b.status !== 'closed'),
                    )
                    .map((b) => (
                      <option key={b.name} value={b.name}>
                        {/* The same words the top bar's chip uses. Two
                            surfaces naming one draft two ways is how somebody
                            concludes there are two drafts. */}
                        {draftLabel(b.name, b)}
                      </option>
                    ))}
                </select>
              </div>
              <button className="btn btn--ghost btn--sm" onClick={onNewDraft}>
                <Icon name="plus" size={13} />
                New draft
              </button>
              {aimedAt && <span className="badge badge--quiet">merges into {aimedAt}</span>}
            </div>
          </div>
          <button className="btn btn--ghost btn--icon" onClick={onClose}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>

        <div className="drawer__body">
          <section className="drawer__block">
            <div className="label">
              {staged.length} change{staged.length === 1 ? '' : 's'}
            </div>
            {staged.length === 0 ? (
              <div className="drawer__none">
                <p className="nothing">Nothing staged yet. Edit a page and save it.</p>
                {onWrite && (
                  <button
                    className="btn btn--tinted btn--sm"
                    onClick={() => {
                      onClose()
                      onWrite()
                    }}
                  >
                    <Icon name="page" size={13} />
                    Open a page
                  </button>
                )}
              </div>
            ) : (
              <ul className="filelist">
                {staged.map((s) => (
                  <li key={s.path} className="filelist__row">
                    <span className={`dot dot--${s.op}`} />
                    <span className="filelist__path truncate" title={s.path}>
                      {s.path}
                    </span>
                    <span className="filelist__op">{VERB[s.op]}</span>
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => onUnstage(s.path)}
                      disabled={busy}
                      data-tip="Discard this staged change"
                    >
                      Discard
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {blocking.length > 0 && (
            <section className="drawer__block">
              <div className="label">Blocking the commit</div>
              <ul className="problemlist">
                {blocking.map((p, i) => (
                  <li key={i} className="problemlist__row">
                    <Icon name="alert" size={13} />
                    <span>
                      <span className="mono">{p.file}</span> — {p.message}
                    </span>
                  </li>
                ))}
              </ul>
              {/*
                The repair, when the product holds one for what is blocking.

                An import can arrive with `redirect_from` already pointing at an
                address another page has since taken, and the whole draft is
                then unpublishable. The fix has existed in the core from the
                start and had no door, so the way past was to open every page
                and edit YAML — in a product whose reason for existing is that
                nobody should have to. Which problems it repairs is the SERVER'S
                answer, not a guess made here.
              */}
              {onTidyRedirects && repairable.length > 0 && (
                <button
                  className="btn btn--tinted btn--sm"
                  disabled={busy}
                  onClick={() => onTidyRedirects()}
                >
                  <Icon name="link" size={13} />
                  Remove the{' '}
                  {repairable.length === 1 ? 'redirect' : `${repairable.length} redirects`} that
                  cannot work
                </button>
              )}
              <p className="drawer__hint">
                {onTidyRedirects && repairable.length > 0
                  ? repairable.length === blocking.length
                    ? 'It is staged as a change like any other, so you can read it in the diff before committing.'
                    : 'That clears the redirects. The rest are decisions only you can make.'
                  : 'The server refuses these too, so fixing them here is the only way forward.'}
              </p>
            </section>
          )}

          <section className="drawer__block">
            <div className="label">
              Review
              {thread && thread.status !== 'none' && (
                <span className={`badge review-badge review-badge--${thread.status}`}>
                  {thread.status === 'requested' && 'Review requested'}
                  {thread.status === 'approved' && 'Approved'}
                  {thread.status === 'changes_requested' && 'Changes requested'}
                </span>
              )}
            </div>

            {thread && thread.events.length > 0 && (
              <ul className="review-thread">
                {thread.events.map((e) => (
                  <li key={e.id} className={`review-thread__row review-thread__row--${e.kind}`}>
                    <span className="review-thread__who">{e.actorName}</span>
                    <span className="review-thread__what">
                      {e.kind === 'request' && 'asked for a review'}
                      {e.kind === 'comment' && 'commented'}
                      {e.kind === 'approve' && 'approved'}
                      {e.kind === 'request_changes' && 'asked for changes'}
                    </span>
                    {e.body && <span className="review-thread__body">{e.body}</span>}
                  </li>
                ))}
              </ul>
            )}

            {thread?.status === 'none' && (
              <p className="drawer__hint">
                {committed
                  ? 'Committed and ready for a second pair of eyes.'
                  : 'Commit the draft first — a review needs something to review.'}
              </p>
            )}

            <div className="review-actions">
              {thread?.status !== 'approved' && (
                <input
                  className="input"
                  placeholder={
                    thread && thread.events.length > 0
                      ? 'Reply…'
                      : 'Anything the reviewer should know?'
                  }
                  value={note}
                  disabled={reviewBusy}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
              {branch && thread?.status === 'none' && (
                <>
                  <select
                    className="input"
                    aria-label="Reviewer"
                    value={reviewer}
                    disabled={reviewBusy}
                    onChange={(event) => setReviewer(event.target.value)}
                  >
                    <option value="">Choose a reviewer…</option>
                    {people
                      .filter(
                        (person) =>
                          person.userId !== youId &&
                          (person.role === 'owner' ||
                            person.role === 'admin' ||
                            person.canReview ||
                            person.canPublish),
                      )
                      .map((person) => (
                        <option key={person.userId} value={person.userId}>
                          {person.displayName}
                        </option>
                      ))}
                  </select>
                  <button
                    className="btn btn--primary btn--sm"
                    disabled={!committed || !reviewer || reviewBusy || busy}
                    onClick={() =>
                      void act(() => api.requestReview(branch, note.trim() || undefined, reviewer))
                    }
                  >
                    <Icon name="eye" size={13} />
                    Request review
                  </button>
                </>
              )}
              {branch && thread && thread.status !== 'none' && thread.status !== 'approved' && (
                <button
                  className="btn btn--outline btn--sm"
                  disabled={reviewBusy || note.trim().length === 0}
                  onClick={() => void act(() => api.reviewComment(branch, note.trim()))}
                >
                  Comment
                </button>
              )}
              {/*
                The verdict carries the commit THIS PANEL was drawn from.

                `readSha` is the sha of the words on the screen, not the sha of
                the branch at the moment of pressing — and those two differ
                exactly when the author committed while the reviewer was
                reading. The server refuses that verdict rather than recording
                an opinion about words nobody read.

                A refusal deliberately does NOT refresh it. If it did, a second
                press would bless the new commit unread, which is the defect
                wearing a retry button. Reopening the drawer is what re-reads —
                and reopening also redraws the diff, so the reviewer sees the
                new words before judging them.
              */}
              {branch &&
                thread &&
                readSha &&
                thread.status === 'requested' &&
                mayVerdict &&
                youId !== null &&
                youId !== thread.authorId && (
                  <>
                    <button
                      className="btn btn--tinted btn--sm"
                      disabled={reviewBusy}
                      onClick={() =>
                        void act(() =>
                          api.reviewVerdict(branch, 'approve', readSha, note.trim() || undefined),
                        )
                      }
                    >
                      <Icon name="check" size={13} />
                      Approve
                    </button>
                    <button
                      className="btn btn--outline btn--sm"
                      disabled={reviewBusy}
                      onClick={() =>
                        void act(() =>
                          api.reviewVerdict(
                            branch,
                            'request_changes',
                            readSha,
                            note.trim() || undefined,
                          ),
                        )
                      }
                    >
                      Ask for changes
                    </button>
                  </>
                )}
            </div>
            {reviewError && <p className="panel__error">{reviewError}</p>}
          </section>

          <section className="drawer__block drawer__block--grow">
            <div className="label labelrow">
              Diff
              <span className="labelrow__tools">
                {diffMode === 'rendered' && changedPages.length > 1 && (
                  <select
                    className="input compare__pick"
                    aria-label="Compare page"
                    value={shownPath ?? ''}
                    onChange={(e) => setComparePath(e.target.value)}
                  >
                    {changedPages.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  className={`btn btn--ghost btn--sm${diffMode === 'rendered' ? ' is-active' : ''}`}
                  onClick={() => setDiffMode('rendered')}
                >
                  Rendered
                </button>
                <button
                  className={`btn btn--ghost btn--sm${diffMode === 'text' ? ' is-active' : ''}`}
                  onClick={() => setDiffMode('text')}
                >
                  Text
                </button>
              </span>
            </div>
            {diffMode === 'text' ? (
              <div className="drawer__diff">
                <DiffView diff={diff} />
              </div>
            ) : !shownPath ? (
              <p className="drawer__hint">
                Rendered comparison shows pages. This draft changes none — the text view has the
                rest.
              </p>
            ) : compareError ? (
              <p className="screen__error">
                <Icon name="alert" size={13} /> The two sides could not be compared: {compareError}
              </p>
            ) : compared === null ? (
              <div className="screen__loading" aria-busy="true">
                <p className="sr-only">Comparing…</p>
                <span className="skeleton" />
                <span className="skeleton" />
                <span className="skeleton" />
              </div>
            ) : (
              <div className="compare">
                <div className="compare__pane">
                  <div className="compare__title">Live site</div>
                  {compared.before != null ? (
                    <div
                      className="preview compare__doc"
                      dangerouslySetInnerHTML={{ __html: compared.before }}
                    />
                  ) : (
                    <p className="drawer__hint">New page — nothing on the live site yet.</p>
                  )}
                </div>
                <div className="compare__pane">
                  <div className="compare__title">This draft</div>
                  {compared.after != null ? (
                    <div
                      className="preview compare__doc"
                      dangerouslySetInnerHTML={{ __html: compared.after }}
                    />
                  ) : (
                    <p className="drawer__hint">Deleted on this draft.</p>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        <footer className="drawer__foot">
          <input
            className="input"
            placeholder="What changed, and why"
            value={message}
            disabled={busy || staged.length === 0}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="drawer__actions">
            {staged.length > 0 && (
              <button
                className="btn btn--primary"
                disabled={!canCommit || busy}
                onClick={() => {
                  onCommit(message.trim())
                  setMessage('')
                }}
                data-tip={canCommit ? 'Commit  ⌘↵' : undefined}
              >
                <Icon name="commit" size={14} />
                Commit changes
              </button>
            )}
            {onMerge &&
              (thread?.status === 'approved' ||
                (thread?.status === 'none' &&
                  people.filter(
                    (person) =>
                      person.userId !== youId &&
                      (person.role === 'owner' ||
                        person.role === 'admin' ||
                        person.canReview ||
                        person.canPublish),
                  ).length === 0)) && (
                <button
                  className="btn btn--publish"
                  disabled={busy || staged.length > 0}
                  onClick={() => {
                    if (!confirming) {
                      setConfirming(true)
                      return
                    }
                    setConfirming(false)
                    onMerge()
                  }}
                >
                  <Icon name="commit" size={14} />
                  {confirming
                    ? `Really merge into ${aimedAt ?? 'main'}?`
                    : thread?.status === 'approved'
                      ? `Merge into ${aimedAt ?? 'main'}`
                      : `Merge into ${aimedAt ?? 'main'} without review`}
                </button>
              )}
            {onMerge && (
              <button
                className="btn btn--outline"
                disabled={busy || sharing}
                onClick={() => void share()}
                data-tip="Build this draft at a link without changing the published site"
              >
                <Icon name="link" size={14} />
                {sharing ? 'Building…' : 'Share preview'}
              </button>
            )}
          </div>
          {/*
            The git vocabulary, folded away.

            Commit, Push and a pull request are real and some teams want them
            — but they are the storage engine's words, and putting them in
            front of every writer made the product's own model (write, then
            publish) look like a four-step ceremony. Publishing is one button
            on the sheet now; this is where the machinery lives for the people
            who keep a repository in step with it.
          */}
          <details className="drawer__advanced">
            <summary>Advanced: push and pull request</summary>
            <div className="drawer__actions">
              <button
                className="btn btn--outline"
                disabled={busy || staged.length > 0}
                onClick={onPush}
                data-tip={staged.length > 0 ? undefined : 'Open a pull request for review'}
              >
                <Icon name="push" size={14} />
                Push
              </button>
              {pr && (
                <a className="btn btn--outline" href={pr.url} target="_blank" rel="noreferrer">
                  PR #{pr.number}
                </a>
              )}
            </div>
          </details>

          {/*
            Why the buttons above are off, in text.

            All three said it in a `title`, and a DISABLED button takes no
            focus — so the answer to "why can I not press this" was reachable
            by hovering and by nothing else, on the panel where the work
            leaves the writer's hands. Worse, one of them was wrong: Commit
            offered "⌘↵" whenever there was staged work and no blocking
            problem, including while it was off because the message box was
            empty, which is a shortcut advertised and inert.

            Shown only while a draft has staged work, which is exactly when
            they are off. `.drawer__hint--foot` is the class the drawers
            already use for a closing line.
          */}
          {staged.length > 0 && (
            <p className="drawer__hint drawer__hint--foot">
              {blocking.length > 0
                ? 'Commit is off until the problems above are fixed. Review and merge wait for the commit.'
                : message.trim().length < 3
                  ? 'Commit is off until the box above says what changed. Review and merge wait for the commit.'
                  : 'Review and merge wait until this draft is committed. ⌘↵ commits.'}
            </p>
          )}

          {shareError && (
            /* Shown here rather than as a toast: the person is looking at this
               panel, and the refusal is about the button they just pressed. */
            <p className="panel__error">{shareError}</p>
          )}

          {link && (
            /* Shown once, and said so. Only the hash is stored, so this panel is
               the only place this link will ever exist. */
            <section className="drawer__block">
              <div className="label">Send this to your reviewer</div>
              <p className="drawer__hint">
                It shows this branch, including work you have not committed, and says on every page
                that it is not the published site. It stops working on{' '}
                {new Date(link.expiresAt).toLocaleDateString()}. This link is shown once — it is not
                stored anywhere.
              </p>
              <div className="invitelink">
                <a
                  className="invitelink__url"
                  href={shareUrl(link.path, siteUrl)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {shareUrl(link.path, siteUrl)}
                </a>
                <button
                  className="btn btn--tinted btn--sm"
                  onClick={() => {
                    void navigator.clipboard.writeText(shareUrl(link.path, siteUrl)).then(
                      () => {
                        setCopied(true)
                        window.setTimeout(() => setCopied(false), 2000)
                      },
                      // Clipboard access can be refused; the URL remains selectable.
                      () => undefined,
                    )
                  }}
                >
                  <Icon name={copied ? 'check' : 'copy'} size={13} />
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            </section>
          )}
          <p className="drawer__hint drawer__hint--foot">
            {onMerge
              ? `Merging updates ${aimedAt ?? 'main'} but does not change the published site. Publishing main is a separate decision.`
              : 'Pushing opens a pull request for somebody to review. Merging needs permission an owner or admin can grant.'}
          </p>
        </footer>
      </aside>
    </div>
  )
}
